import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { MediaManager } from "@/components/MediaManager";
import { toast } from "sonner";
import {
  ArrowUp,
  ArrowDown,
  Columns2,
  Columns3,
  Copy,
  CopyPlus,
  Eye,
  EyeOff,
  Image as ImageIcon,
  LayoutGrid,
  Redo2,
  Rows3,
  Save,
  Sparkles,
  Square,
  Star,
  Trash2,
  Type as TypeIcon,
  Undo2,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  listRichProducts,
  listLiveProducts,
  updateShopSettings,
  type BuilderRow,
  type BuilderColumn,
  type BuilderWidget,
  type BuilderWidgetType,
  type RichProduct,
  type ShopSettings,
} from "@/lib/products";
import { WidgetRenderer } from "./WidgetRenderer";

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

const WIDGET_TEMPLATES: {
  type: BuilderWidgetType;
  label: string;
  icon: React.ReactNode;
  defaults: Record<string, unknown>;
}[] = [
  {
    type: "hero",
    label: "Hero",
    icon: <Sparkles className="h-4 w-4" />,
    defaults: {
      title: "Premium custom print",
      subtitle: "Made for your brand",
      ctaText: "Browse products",
      radius: 32,
    },
  },
  {
    type: "featured",
    label: "Featured products",
    icon: <Star className="h-4 w-4" />,
    defaults: { productIds: [], layout: "grid" },
  },
  {
    type: "product_grid",
    label: "Product grid",
    icon: <LayoutGrid className="h-4 w-4" />,
    defaults: { limit: 12 },
  },
  {
    type: "heading",
    label: "Heading",
    icon: <TypeIcon className="h-4 w-4" />,
    defaults: { text: "Section heading", level: 2, align: "left" },
  },
  {
    type: "text",
    label: "Text",
    icon: <TypeIcon className="h-4 w-4" />,
    defaults: { text: "Tell your customers something useful." },
  },
  {
    type: "image",
    label: "Image",
    icon: <ImageIcon className="h-4 w-4" />,
    defaults: { url: "", rounded: true },
  },
  {
    type: "cta",
    label: "CTA banner",
    icon: <Sparkles className="h-4 w-4" />,
    defaults: {
      title: "Ready to print?",
      subtitle: "Upload your artwork in minutes.",
      buttonText: "Start a job",
      buttonHref: "/app/customer/new-job",
    },
  },
  {
    type: "spacer",
    label: "Spacer",
    icon: <Square className="h-4 w-4" />,
    defaults: { height: 32 },
  },
];

const COLUMN_PRESETS: { label: string; spans: number[]; icon: React.ReactNode }[] = [
  { label: "1 col", spans: [12], icon: <Square className="h-4 w-4" /> },
  { label: "2 col", spans: [6, 6], icon: <Columns2 className="h-4 w-4" /> },
  { label: "3 col", spans: [4, 4, 4], icon: <Columns3 className="h-4 w-4" /> },
  { label: "8 + 4", spans: [8, 4], icon: <Columns2 className="h-4 w-4" /> },
  { label: "4 + 8", spans: [4, 8], icon: <Columns2 className="h-4 w-4" /> },
];

interface Props {
  open: boolean;
  settings: ShopSettings | undefined;
  onClose: () => void;
}

// Deep-clone helper for snapshots / duplicates.
function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v));
}
function reidWidget(w: BuilderWidget): BuilderWidget {
  return { ...clone(w), id: uid() };
}
function reidRow(r: BuilderRow): BuilderRow {
  return {
    id: uid(),
    columns: r.columns.map((c) => ({
      id: uid(),
      span: c.span,
      widgets: c.widgets.map(reidWidget),
    })),
  };
}

export function ShopBuilder({ open, settings, onClose }: Props) {
  const qc = useQueryClient();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const { data: allProducts = [] } = useQuery<RichProduct[]>({
    queryKey: ["rich-products"],
    queryFn: listRichProducts,
    enabled: open,
  });
  const { data: liveProducts = [] } = useQuery<RichProduct[]>({
    queryKey: ["live-products"],
    queryFn: listLiveProducts,
    enabled: open,
  });

  const [rows, setRowsRaw] = useState<BuilderRow[]>([]);
  const [useBuilder, setUseBuilder] = useState(false);
  const [selected, setSelected] = useState<{
    rowId: string;
    colId: string;
    widgetId: string;
  } | null>(null);
  const [showMediaFor, setShowMediaFor] = useState<{ prop: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmGoLive, setConfirmGoLive] = useState(false);

  // ─── History (undo / redo) ────────────────────────────────
  const past = useRef<BuilderRow[][]>([]);
  const future = useRef<BuilderRow[][]>([]);
  const [, forceHistoryTick] = useState(0);

  const commit = useCallback((updater: (prev: BuilderRow[]) => BuilderRow[]) => {
    setRowsRaw((prev) => {
      const next = updater(prev);
      past.current.push(clone(prev));
      if (past.current.length > 100) past.current.shift();
      future.current = [];
      forceHistoryTick((t) => t + 1);
      return next;
    });
  }, []);

  const undo = useCallback(() => {
    if (!past.current.length) return;
    setRowsRaw((curr) => {
      const prev = past.current.pop()!;
      future.current.push(clone(curr));
      forceHistoryTick((t) => t + 1);
      return prev;
    });
  }, []);

  const redo = useCallback(() => {
    if (!future.current.length) return;
    setRowsRaw((curr) => {
      const next = future.current.pop()!;
      past.current.push(clone(curr));
      forceHistoryTick((t) => t + 1);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!open || !settings) return;
    setRowsRaw(Array.isArray(settings.builder_rows) ? settings.builder_rows : []);
    setUseBuilder(!!settings.use_builder);
    past.current = [];
    future.current = [];
    forceHistoryTick((t) => t + 1);
  }, [open, settings]);

  // Keyboard shortcuts: Cmd/Ctrl+Z (undo), Cmd/Ctrl+Shift+Z or Ctrl+Y (redo).
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((k === "z" && e.shiftKey) || k === "y") {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, undo, redo]);

  const selectedWidget = useMemo<BuilderWidget | null>(() => {
    if (!selected) return null;
    const row = rows.find((r) => r.id === selected.rowId);
    const col = row?.columns.find((c) => c.id === selected.colId);
    return col?.widgets.find((w) => w.id === selected.widgetId) ?? null;
  }, [selected, rows]);

  // ─── Mutators (all go through commit) ─────────────────────
  const addRow = (spans: number[] = [12]) => {
    const row: BuilderRow = {
      id: uid(),
      columns: spans.map((span) => ({ id: uid(), span, widgets: [] })),
    };
    commit((r) => [...r, row]);
  };

  const deleteRow = (rowId: string) => {
    commit((r) => r.filter((x) => x.id !== rowId));
    if (selected?.rowId === rowId) setSelected(null);
  };

  const duplicateRow = (rowId: string) => {
    commit((rs) => {
      const i = rs.findIndex((r) => r.id === rowId);
      if (i < 0) return rs;
      const copy = reidRow(rs[i]);
      const next = [...rs];
      next.splice(i + 1, 0, copy);
      return next;
    });
    toast.success("Row duplicated");
  };

  const moveRow = (rowId: string, dir: -1 | 1) => {
    commit((r) => {
      const i = r.findIndex((x) => x.id === rowId);
      if (i < 0) return r;
      const j = i + dir;
      if (j < 0 || j >= r.length) return r;
      const next = [...r];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const setRowSpans = (rowId: string, spans: number[]) => {
    commit((rs) =>
      rs.map((r) => {
        if (r.id !== rowId) return r;
        const next: BuilderColumn[] = spans.map((span, i) => {
          const existing = r.columns[i];
          return existing ? { ...existing, span } : { id: uid(), span, widgets: [] };
        });
        if (r.columns.length > spans.length) {
          const overflow = r.columns.slice(spans.length).flatMap((c) => c.widgets);
          next[next.length - 1] = {
            ...next[next.length - 1],
            widgets: [...next[next.length - 1].widgets, ...overflow],
          };
        }
        return { ...r, columns: next };
      }),
    );
  };

  const addWidget = (rowId: string, colId: string, type: BuilderWidgetType) => {
    const tmpl = WIDGET_TEMPLATES.find((t) => t.type === type)!;
    const widget: BuilderWidget = { id: uid(), type, props: { ...tmpl.defaults } };
    commit((rs) =>
      rs.map((r) =>
        r.id !== rowId
          ? r
          : {
              ...r,
              columns: r.columns.map((c) =>
                c.id !== colId ? c : { ...c, widgets: [...c.widgets, widget] },
              ),
            },
      ),
    );
    setSelected({ rowId, colId, widgetId: widget.id });
  };

  const deleteWidget = (rowId: string, colId: string, widgetId: string) => {
    commit((rs) =>
      rs.map((r) =>
        r.id !== rowId
          ? r
          : {
              ...r,
              columns: r.columns.map((c) =>
                c.id !== colId ? c : { ...c, widgets: c.widgets.filter((w) => w.id !== widgetId) },
              ),
            },
      ),
    );
    if (selected?.widgetId === widgetId) setSelected(null);
  };

  const duplicateWidget = (rowId: string, colId: string, widgetId: string) => {
    let newId = "";
    commit((rs) =>
      rs.map((r) =>
        r.id !== rowId
          ? r
          : {
              ...r,
              columns: r.columns.map((c) => {
                if (c.id !== colId) return c;
                const idx = c.widgets.findIndex((w) => w.id === widgetId);
                if (idx < 0) return c;
                const copy = reidWidget(c.widgets[idx]);
                newId = copy.id;
                const widgets = [...c.widgets];
                widgets.splice(idx + 1, 0, copy);
                return { ...c, widgets };
              }),
            },
      ),
    );
    if (newId) setSelected({ rowId, colId, widgetId: newId });
    toast.success("Widget duplicated");
  };

  const updateWidgetProps = (patch: Record<string, unknown>) => {
    if (!selected) return;
    commit((rs) =>
      rs.map((r) =>
        r.id !== selected.rowId
          ? r
          : {
              ...r,
              columns: r.columns.map((c) =>
                c.id !== selected.colId
                  ? c
                  : {
                      ...c,
                      widgets: c.widgets.map((w) =>
                        w.id !== selected.widgetId ? w : { ...w, props: { ...w.props, ...patch } },
                      ),
                    },
              ),
            },
      ),
    );
  };

  // ─── Drag-and-drop: reorder within column + cross-column move ─────────────
  const findWidget = useCallback((widgetId: string, rs: BuilderRow[]) => {
    for (const r of rs) {
      for (const c of r.columns) {
        const i = c.widgets.findIndex((w) => w.id === widgetId);
        if (i >= 0) return { rowId: r.id, colId: c.id, index: i };
      }
    }
    return null;
  }, []);

  const onDragEnd = (e: DragEndEvent) => {
    const activeRaw = String(e.active.id);
    const overRaw = e.over?.id ? String(e.over.id) : null;
    if (!overRaw) return;
    // active is always "w:rowId:colId:widgetId"
    const [, , , widgetId] = activeRaw.split(":");

    commit((rs) => {
      const src = findWidget(widgetId, rs);
      if (!src) return rs;

      let dstRowId: string, dstColId: string, dstIndex: number;
      if (overRaw.startsWith("w:")) {
        const [, , , overWidgetId] = overRaw.split(":");
        if (overWidgetId === widgetId) return rs;
        const dst = findWidget(overWidgetId, rs);
        if (!dst) return rs;
        dstRowId = dst.rowId;
        dstColId = dst.colId;
        dstIndex = dst.index;
      } else if (overRaw.startsWith("drop:")) {
        const [, rId, cId] = overRaw.split(":");
        dstRowId = rId;
        dstColId = cId;
        const col = rs.find((r) => r.id === rId)?.columns.find((c) => c.id === cId);
        dstIndex = col?.widgets.length ?? 0;
      } else return rs;

      // Same column reorder.
      if (src.rowId === dstRowId && src.colId === dstColId) {
        return rs.map((r) =>
          r.id !== src.rowId
            ? r
            : {
                ...r,
                columns: r.columns.map((c) =>
                  c.id !== src.colId
                    ? c
                    : { ...c, widgets: arrayMove(c.widgets, src.index, dstIndex) },
                ),
              },
        );
      }

      // Cross-column move.
      let moved: BuilderWidget | null = null;
      const stripped = rs.map((r) =>
        r.id !== src.rowId
          ? r
          : {
              ...r,
              columns: r.columns.map((c) => {
                if (c.id !== src.colId) return c;
                const widgets = [...c.widgets];
                moved = widgets.splice(src.index, 1)[0] ?? null;
                return { ...c, widgets };
              }),
            },
      );
      if (!moved) return rs;
      return stripped.map((r) =>
        r.id !== dstRowId
          ? r
          : {
              ...r,
              columns: r.columns.map((c) => {
                if (c.id !== dstColId) return c;
                const widgets = [...c.widgets];
                widgets.splice(Math.min(dstIndex, widgets.length), 0, moved as BuilderWidget);
                return { ...c, widgets };
              }),
            },
      );
    });
  };

  const handleSave = async (nextUseBuilder = useBuilder) => {
    setSaving(true);
    try {
      await updateShopSettings({ builder_rows: rows, use_builder: nextUseBuilder });
      qc.invalidateQueries({ queryKey: ["shop-settings"] });
      toast.success("Shop builder saved");
      onClose();
    } catch (err) {
      toast.error((err as Error).message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const ctx = { liveProducts };
  const canUndo = past.current.length > 0;
  const canRedo = future.current.length > 0;

  // Toggle handler: warn before going live; turning off is instant.
  const onToggleLive = (v: boolean) => {
    if (v && !useBuilder) setConfirmGoLive(true);
    else setUseBuilder(v);
  };

  return (
    <>
      {showMediaFor && (
        <MediaManager
          onClose={() => setShowMediaFor(null)}
          onSelect={(url) => {
            updateWidgetProps({ [showMediaFor.prop]: url });
            setShowMediaFor(null);
          }}
        />
      )}

      <AlertDialog open={confirmGoLive} onOpenChange={setConfirmGoLive}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish builder layout to customers?</AlertDialogTitle>
            <AlertDialogDescription>
              The current builder canvas will replace the old shop layout for every customer. Make
              sure you've previewed it and saved. You can switch back at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep as draft</AlertDialogCancel>
            <AlertDialogAction onClick={() => setUseBuilder(true)}>
              Yes, publish it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-[1600px] w-[98vw] h-[94vh] p-0 gap-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-5 py-3 border-b border-border flex-row items-center justify-between gap-3 space-y-0">
            <DialogTitle className="text-base flex items-center gap-2">
              <LayoutGrid className="h-4 w-4 text-primary" /> Shop page builder
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={undo}
                disabled={!canUndo}
                className="rounded-full gap-1.5 h-8"
              >
                <Undo2 className="h-3.5 w-3.5" /> Undo
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={redo}
                disabled={!canRedo}
                className="rounded-full gap-1.5 h-8"
              >
                <Redo2 className="h-3.5 w-3.5" /> Redo
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPreviewOpen((v) => !v)}
                className="rounded-full gap-1.5 h-8"
              >
                {previewOpen ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {previewOpen ? "Hide preview" : "Show preview"}
              </Button>
              <label className="flex items-center gap-2 text-xs text-muted-foreground pl-2 border-l border-border ml-1">
                <Switch checked={useBuilder} onCheckedChange={onToggleLive} />
                {useBuilder ? "Builder layout is live" : "Old layout is live (builder is draft)"}
              </label>
              <Button
                size="sm"
                onClick={() => handleSave()}
                disabled={saving}
                className="rounded-full gap-1.5"
              >
                <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </DialogHeader>

          <DndContext sensors={sensors} onDragEnd={onDragEnd}>
            <div
              className={cn(
                "grid flex-1 min-h-0",
                previewOpen ? "grid-cols-[200px_1fr_1fr]" : "grid-cols-[220px_1fr_300px]",
              )}
            >
              {/* Left palette */}
              <aside className="border-r border-border bg-muted/30 overflow-y-auto p-3 space-y-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
                    Add a row
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {COLUMN_PRESETS.map((preset) => (
                      <button
                        key={preset.label}
                        onClick={() => addRow(preset.spans)}
                        className="flex items-center gap-1.5 rounded-xl border border-border bg-card p-2 text-[11px] hover:border-primary/40 transition-colors"
                      >
                        {preset.icon}
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
                    Widgets
                  </div>
                  <p className="text-[10px] text-muted-foreground mb-2">
                    Click a column on the canvas, then click a widget to add it.
                  </p>
                  <div className="space-y-1">
                    {WIDGET_TEMPLATES.map((t) => (
                      <button
                        key={t.type}
                        onClick={() => {
                          if (selected) {
                            addWidget(selected.rowId, selected.colId, t.type);
                            return;
                          }
                          const rowId = uid();
                          const colId = uid();
                          const widget: BuilderWidget = {
                            id: uid(),
                            type: t.type,
                            props: { ...t.defaults },
                          };
                          commit((r) => [
                            ...r,
                            { id: rowId, columns: [{ id: colId, span: 12, widgets: [widget] }] },
                          ]);
                          setSelected({ rowId, colId, widgetId: widget.id });
                        }}
                        className="flex w-full items-center gap-2 rounded-xl border border-border bg-card p-2 text-xs hover:border-primary/40 transition-colors"
                      >
                        <span className="text-primary">{t.icon}</span>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              </aside>

              {/* Canvas */}
              <main className="overflow-y-auto bg-background p-6">
                {rows.length === 0 ? (
                  <div className="flex flex-col items-center justify-center text-center py-24 rounded-3xl border border-dashed border-border">
                    <Rows3 className="h-8 w-8 text-muted-foreground" />
                    <p className="mt-3 text-sm font-medium">Empty canvas</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Add a row from the left to start designing your shop.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {rows.map((row, ri) => (
                      <RowBlock
                        key={row.id}
                        row={row}
                        index={ri}
                        total={rows.length}
                        selected={selected}
                        ctx={ctx}
                        onSelect={(colId, widgetId) =>
                          setSelected({ rowId: row.id, colId, widgetId })
                        }
                        onSelectColumn={(colId) =>
                          setSelected({ rowId: row.id, colId, widgetId: "" })
                        }
                        onDeleteWidget={(colId, widgetId) => deleteWidget(row.id, colId, widgetId)}
                        onDuplicateWidget={(colId, widgetId) =>
                          duplicateWidget(row.id, colId, widgetId)
                        }
                        onDeleteRow={() => deleteRow(row.id)}
                        onDuplicateRow={() => duplicateRow(row.id)}
                        onMoveRow={(d) => moveRow(row.id, d)}
                        onChangeSpans={(spans) => setRowSpans(row.id, spans)}
                      />
                    ))}
                  </div>
                )}
              </main>

              {/* Right panel: settings OR live customer preview */}
              <aside
                className={cn(
                  "border-l border-border overflow-y-auto",
                  previewOpen ? "bg-background" : "bg-muted/30 p-4",
                )}
              >
                {previewOpen ? (
                  <LivePreview rows={rows} ctx={ctx} />
                ) : !selectedWidget ? (
                  <div className="text-xs text-muted-foreground">
                    Click a widget on the canvas to edit its settings.
                  </div>
                ) : (
                  <WidgetSettings
                    widget={selectedWidget}
                    allProducts={allProducts}
                    onChange={updateWidgetProps}
                    onPickMedia={(prop) => setShowMediaFor({ prop })}
                    onDuplicate={() => {
                      if (!selected) return;
                      duplicateWidget(selected.rowId, selected.colId, selected.widgetId);
                    }}
                    onDelete={() => {
                      if (!selected) return;
                      deleteWidget(selected.rowId, selected.colId, selected.widgetId);
                    }}
                  />
                )}
              </aside>
            </div>
          </DndContext>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Live customer preview pane ─────────────────────────────────────────────

function LivePreview({ rows, ctx }: { rows: BuilderRow[]; ctx: { liveProducts: RichProduct[] } }) {
  return (
    <div className="h-full flex flex-col">
      <div className="px-3 py-2 border-b border-border bg-muted/30 flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
        <Eye className="h-3 w-3" /> Customer preview · updates live
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {rows.length === 0 && (
          <div className="text-xs text-muted-foreground text-center py-12">
            Add widgets to see them here.
          </div>
        )}
        {rows.map((row) => (
          <div key={row.id} className="grid grid-cols-12 gap-4">
            {row.columns.map((col) => (
              <div
                key={col.id}
                style={{ gridColumn: `span ${col.span} / span ${col.span}` }}
                className="space-y-3"
              >
                {col.widgets.map((w) => (
                  <WidgetRenderer key={w.id} widget={w} ctx={ctx} />
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Row / column / widget canvas blocks ─────────────────────────────────────

function RowBlock({
  row,
  index,
  total,
  selected,
  ctx,
  onSelect,
  onSelectColumn,
  onDeleteWidget,
  onDuplicateWidget,
  onDeleteRow,
  onDuplicateRow,
  onMoveRow,
  onChangeSpans,
}: {
  row: BuilderRow;
  index: number;
  total: number;
  selected: { rowId: string; colId: string; widgetId: string } | null;
  ctx: { liveProducts: RichProduct[] };
  onSelect: (colId: string, widgetId: string) => void;
  onSelectColumn: (colId: string) => void;
  onDeleteWidget: (colId: string, widgetId: string) => void;
  onDuplicateWidget: (colId: string, widgetId: string) => void;
  onDeleteRow: () => void;
  onDuplicateRow: () => void;
  onMoveRow: (dir: -1 | 1) => void;
  onChangeSpans: (spans: number[]) => void;
}) {
  return (
    <div className="group relative rounded-2xl border border-border bg-card p-3">
      <div className="absolute -top-3 left-3 z-10 flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground shadow-soft">
        <span>Row {index + 1}</span>
      </div>
      <div className="absolute -top-3 right-3 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {COLUMN_PRESETS.map((preset) => (
          <button
            key={preset.label}
            onClick={() => onChangeSpans(preset.spans)}
            title={preset.label}
            className={cn(
              "rounded-full border border-border bg-background p-1 text-muted-foreground hover:text-foreground hover:border-primary/40 shadow-soft",
              JSON.stringify(row.columns.map((c) => c.span)) === JSON.stringify(preset.spans) &&
                "border-primary text-primary",
            )}
          >
            {preset.icon}
          </button>
        ))}
        <button
          onClick={() => onMoveRow(-1)}
          disabled={index === 0}
          title="Move row up"
          className="rounded-full border border-border bg-background p-1 text-muted-foreground hover:text-foreground disabled:opacity-40 shadow-soft"
        >
          <ArrowUp className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => onMoveRow(1)}
          disabled={index === total - 1}
          title="Move row down"
          className="rounded-full border border-border bg-background p-1 text-muted-foreground hover:text-foreground disabled:opacity-40 shadow-soft"
        >
          <ArrowDown className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={onDuplicateRow}
          title="Copy row"
          className="rounded-full border border-border bg-background p-1 text-muted-foreground hover:text-foreground hover:border-primary/40 shadow-soft"
        >
          <CopyPlus className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={onDeleteRow}
          title="Delete row"
          className="rounded-full border border-border bg-background p-1 text-destructive hover:bg-destructive/10 shadow-soft"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-12 gap-3">
        {row.columns.map((col) => (
          <ColumnBlock
            key={col.id}
            row={row}
            col={col}
            selected={selected}
            ctx={ctx}
            onSelect={(widgetId) => onSelect(col.id, widgetId)}
            onSelectEmpty={() => onSelectColumn(col.id)}
            onDeleteWidget={(widgetId) => onDeleteWidget(col.id, widgetId)}
            onDuplicateWidget={(widgetId) => onDuplicateWidget(col.id, widgetId)}
          />
        ))}
      </div>
    </div>
  );
}

function ColumnBlock({
  row,
  col,
  selected,
  ctx,
  onSelect,
  onSelectEmpty,
  onDeleteWidget,
  onDuplicateWidget,
}: {
  row: BuilderRow;
  col: BuilderColumn;
  selected: { rowId: string; colId: string; widgetId: string } | null;
  ctx: { liveProducts: RichProduct[] };
  onSelect: (widgetId: string) => void;
  onSelectEmpty: () => void;
  onDeleteWidget: (widgetId: string) => void;
  onDuplicateWidget: (widgetId: string) => void;
}) {
  const dropId = `drop:${row.id}:${col.id}`;
  const { setNodeRef, isOver } = useDroppable({ id: dropId });
  const isSelectedColumn =
    selected?.rowId === row.id && selected?.colId === col.id && !selected?.widgetId;
  const sortableIds = col.widgets.map((w) => `w:${row.id}:${col.id}:${w.id}`);
  return (
    <div
      ref={setNodeRef}
      onClick={(e) => {
        if (col.widgets.length === 0) {
          e.stopPropagation();
          onSelectEmpty();
        }
      }}
      className={cn(
        "min-h-[100px] rounded-xl border border-dashed p-2 space-y-2 transition-colors",
        isOver ? "border-primary bg-primary/5" : "border-border/60",
        isSelectedColumn && "border-primary/60 bg-primary/5",
      )}
      style={{ gridColumn: `span ${col.span} / span ${col.span}` }}
    >
      {col.widgets.length === 0 && (
        <div className="flex h-full min-h-[80px] items-center justify-center text-[11px] text-muted-foreground">
          Empty column — click then add a widget
        </div>
      )}
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        {col.widgets.map((widget) => (
          <SortableWidget
            key={widget.id}
            row={row}
            col={col}
            widget={widget}
            ctx={ctx}
            isSelected={selected?.widgetId === widget.id}
            onSelect={() => onSelect(widget.id)}
            onDelete={() => onDeleteWidget(widget.id)}
            onDuplicate={() => onDuplicateWidget(widget.id)}
          />
        ))}
      </SortableContext>
    </div>
  );
}

function SortableWidget({
  row,
  col,
  widget,
  ctx,
  isSelected,
  onSelect,
  onDelete,
  onDuplicate,
}: {
  row: BuilderRow;
  col: BuilderColumn;
  widget: BuilderWidget;
  ctx: { liveProducts: RichProduct[] };
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  const dragId = `w:${row.id}:${col.id}:${widget.id}`;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: dragId,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative rounded-lg border bg-background p-2 transition-shadow",
        isSelected
          ? "border-primary ring-2 ring-primary/30"
          : "border-border hover:border-primary/40",
        isDragging && "opacity-60 shadow-lg z-20",
      )}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      <div className="absolute -top-2 left-2 z-10 flex items-center gap-1 rounded-full border border-border bg-background px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-muted-foreground">
        <span {...listeners} {...attributes} className="cursor-grab active:cursor-grabbing px-0.5">
          ⋮⋮
        </span>
        <span>{widget.type}</span>
      </div>
      <div className="absolute -top-2 right-2 z-10 flex items-center gap-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate();
          }}
          title="Duplicate widget"
          className="rounded-full border border-border bg-background p-0.5 text-muted-foreground hover:text-foreground hover:border-primary/40"
        >
          <Copy className="h-3 w-3" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title="Delete widget"
          className="rounded-full border border-border bg-background p-0.5 text-destructive hover:bg-destructive/10"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
      <div className="pointer-events-none">
        <WidgetRenderer widget={widget} ctx={ctx} />
      </div>
    </div>
  );
}

// ─── Right-panel settings ────────────────────────────────────────────────────

function WidgetSettings({
  widget,
  allProducts,
  onChange,
  onPickMedia,
  onDuplicate,
  onDelete,
}: {
  widget: BuilderWidget;
  allProducts: RichProduct[];
  onChange: (patch: Record<string, unknown>) => void;
  onPickMedia: (prop: string) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const p = widget.props ?? {};
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {widget.type}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onDuplicate}
            className="text-xs text-foreground/80 flex items-center gap-1 hover:underline"
          >
            <Copy className="h-3 w-3" /> Duplicate
          </button>
          <button
            onClick={onDelete}
            className="text-xs text-destructive flex items-center gap-1 hover:underline"
          >
            <Trash2 className="h-3 w-3" /> Delete
          </button>
        </div>
      </div>

      {widget.type === "hero" && (
        <>
          <Field label="Title">
            <Input value={p.title ?? ""} onChange={(e) => onChange({ title: e.target.value })} />
          </Field>
          <Field label="Subtitle">
            <Input
              value={p.subtitle ?? ""}
              onChange={(e) => onChange({ subtitle: e.target.value })}
            />
          </Field>
          <Field label="CTA text">
            <Input
              value={p.ctaText ?? ""}
              onChange={(e) => onChange({ ctaText: e.target.value })}
            />
          </Field>
          <Field label="CTA link">
            <Input
              value={p.ctaHref ?? ""}
              onChange={(e) => onChange({ ctaHref: e.target.value })}
              placeholder="/app/customer/shop"
            />
          </Field>
          <Field label="Background image">
            <div className="flex items-center gap-2">
              {p.imageUrl && (
                <img
                  src={p.imageUrl}
                  alt=""
                  className="h-10 w-16 rounded-md object-cover border border-border"
                />
              )}
              <Button size="sm" variant="outline" onClick={() => onPickMedia("imageUrl")}>
                {p.imageUrl ? "Change" : "Choose"}
              </Button>
              {p.imageUrl && (
                <button
                  onClick={() => onChange({ imageUrl: null })}
                  className="text-xs text-muted-foreground underline"
                >
                  Remove
                </button>
              )}
            </div>
          </Field>
          <RangeField
            label={`Min height: ${p.height ?? 0}px`}
            min={0}
            max={700}
            step={20}
            value={p.height ?? 0}
            onChange={(v) => onChange({ height: v })}
          />
          <RangeField
            label={`Corner radius: ${p.radius ?? 32}px`}
            min={0}
            max={60}
            step={4}
            value={p.radius ?? 32}
            onChange={(v) => onChange({ radius: v })}
          />
        </>
      )}

      {widget.type === "featured" && (
        <>
          <Field label="Layout">
            <div className="grid grid-cols-3 gap-1.5">
              {["grid", "bento", "carousel"].map((v) => (
                <button
                  key={v}
                  onClick={() => onChange({ layout: v })}
                  className={cn(
                    "rounded-lg border py-1 text-xs",
                    p.layout === v ? "border-primary text-primary bg-primary/5" : "border-border",
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Pick products">
            <div className="max-h-72 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
              {allProducts.map((prod) => {
                const selected = Array.isArray(p.productIds) && p.productIds.includes(prod.id);
                return (
                  <button
                    key={prod.id}
                    onClick={() => {
                      const ids: string[] = Array.isArray(p.productIds) ? [...p.productIds] : [];
                      const i = ids.indexOf(prod.id);
                      if (i >= 0) ids.splice(i, 1);
                      else ids.push(prod.id);
                      onChange({ productIds: ids });
                    }}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md p-1.5 text-left text-xs",
                      selected ? "bg-primary/10 text-primary" : "hover:bg-muted",
                    )}
                  >
                    {prod.image_main_url ? (
                      <img
                        src={prod.image_main_url}
                        alt=""
                        className="h-6 w-6 shrink-0 rounded object-cover"
                      />
                    ) : (
                      <div className="h-6 w-6 shrink-0 rounded bg-muted" />
                    )}
                    <span className="truncate">{prod.name}</span>
                  </button>
                );
              })}
              {allProducts.length === 0 && (
                <p className="text-xs text-muted-foreground">No products yet.</p>
              )}
            </div>
          </Field>
        </>
      )}

      {widget.type === "product_grid" && (
        <RangeField
          label={`Show up to: ${p.limit ?? 12} products`}
          min={4}
          max={48}
          step={4}
          value={p.limit ?? 12}
          onChange={(v) => onChange({ limit: v })}
        />
      )}

      {widget.type === "heading" && (
        <>
          <Field label="Text">
            <Input value={p.text ?? ""} onChange={(e) => onChange({ text: e.target.value })} />
          </Field>
          <Field label="Level">
            <div className="grid grid-cols-4 gap-1">
              {[1, 2, 3, 4].map((lv) => (
                <button
                  key={lv}
                  onClick={() => onChange({ level: lv })}
                  className={cn(
                    "rounded-lg border py-1 text-xs",
                    (p.level ?? 2) === lv
                      ? "border-primary text-primary bg-primary/5"
                      : "border-border",
                  )}
                >
                  H{lv}
                </button>
              ))}
            </div>
          </Field>
          <AlignField value={p.align ?? "left"} onChange={(v) => onChange({ align: v })} />
        </>
      )}

      {widget.type === "text" && (
        <>
          <Field label="Text">
            <textarea
              value={p.text ?? ""}
              onChange={(e) => onChange({ text: e.target.value })}
              rows={6}
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
            />
          </Field>
          <AlignField value={p.align ?? "left"} onChange={(v) => onChange({ align: v })} />
        </>
      )}

      {widget.type === "image" && (
        <>
          <Field label="Image">
            <div className="flex items-center gap-2">
              {p.url && (
                <img
                  src={p.url}
                  alt=""
                  className="h-10 w-16 rounded-md object-cover border border-border"
                />
              )}
              <Button size="sm" variant="outline" onClick={() => onPickMedia("url")}>
                {p.url ? "Change" : "Choose"}
              </Button>
            </div>
          </Field>
          <Field label="Alt text">
            <Input value={p.alt ?? ""} onChange={(e) => onChange({ alt: e.target.value })} />
          </Field>
          <Field label="Rounded corners">
            <Switch checked={p.rounded ?? true} onCheckedChange={(v) => onChange({ rounded: v })} />
          </Field>
          <RangeField
            label={`Max height: ${(p.maxHeight ?? 0) ? `${p.maxHeight}px` : "auto"}`}
            min={0}
            max={800}
            step={20}
            value={p.maxHeight ?? 0}
            onChange={(v) => onChange({ maxHeight: v || null })}
          />
        </>
      )}

      {widget.type === "cta" && (
        <>
          <Field label="Title">
            <Input value={p.title ?? ""} onChange={(e) => onChange({ title: e.target.value })} />
          </Field>
          <Field label="Subtitle">
            <Input
              value={p.subtitle ?? ""}
              onChange={(e) => onChange({ subtitle: e.target.value })}
            />
          </Field>
          <Field label="Button text">
            <Input
              value={p.buttonText ?? ""}
              onChange={(e) => onChange({ buttonText: e.target.value })}
            />
          </Field>
          <Field label="Button link">
            <Input
              value={p.buttonHref ?? ""}
              onChange={(e) => onChange({ buttonHref: e.target.value })}
            />
          </Field>
        </>
      )}

      {widget.type === "spacer" && (
        <RangeField
          label={`Height: ${p.height ?? 32}px`}
          min={8}
          max={240}
          step={8}
          value={p.height ?? 32}
          onChange={(v) => onChange({ height: v })}
        />
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-xs font-medium">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function RangeField({
  label,
  min,
  max,
  step,
  value,
  onChange,
}: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <Field label={label}>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full"
      />
    </Field>
  );
}

function AlignField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Field label="Alignment">
      <div className="grid grid-cols-3 gap-1">
        {["left", "center", "right"].map((a) => (
          <button
            key={a}
            onClick={() => onChange(a)}
            className={cn(
              "rounded-lg border py-1 text-xs capitalize",
              value === a ? "border-primary text-primary bg-primary/5" : "border-border",
            )}
          >
            {a}
          </button>
        ))}
      </div>
    </Field>
  );
}
